/**
 * Settings. `SHELL-018` (already satisfied), the Install section
 * (`PWA-006`–`PWA-011`), Storage (`PWA-053`–`055`) and Troubleshooting
 * (`PWA-062`), Diagnostics (`PWA-060`, `PWA-061`).
 *
 * Minimal on purpose (Sprint 7 stage 1's own brief): a real screen and a
 * section heading pattern, extended by stage 5 and stage 6 with exactly the
 * sections their own requirements need. `SHELL-060`–`065` (sound, music,
 * reduced motion, export/import, reset-with-confirmation) are not built
 * here and stay unticked -- see the stage 1 outcome for why the brief's own
 * "out of scope" table and `docs/roadmap.md`'s Sprint 8 checklist disagree
 * about whether the sound controls belong in this stage, and why this file
 * sides with the narrower, explicitly-detailed reading rather than the
 * roadmap's foreshadowing line.
 */

import { useEffect, useState } from 'preact/hooks';

import { Screen } from '@shell/Screen';
import { routes } from '@shell/routes';
import { installAffordance, promptInstall } from '@platform/install';
import { KNOWN_GAME_IDS, gameInfo } from '@platform/game-registry';
import { cachedGameIdsSignal, refreshCachedGameIds } from '@platform/offline-availability';
import { clearCachedGameData } from '@platform/clear-cache';
import { refreshStoragePersisted, storagePersisted } from '@platform/persistent-storage';

export function Settings() {
  const affordance = installAffordance.value;
  const persisted = storagePersisted.value;
  const cached = cachedGameIdsSignal.value;
  const [clearStatus, setClearStatus] = useState<'idle' | 'clearing' | 'cleared'>('idle');

  // PWA-053: read the current grant, not requested here -- a Settings visit
  // must never itself be the moment that asks (PROG-008/PWA-052's own job,
  // on first level completion), only ever report what is already true.
  // PWA-061 reuses the same Cache Storage read `Picker.tsx` already made
  // this signal for -- one read, not a second one this screen owns.
  useEffect(() => {
    refreshStoragePersisted();
    void refreshCachedGameIds();
  }, []);

  const handleClearCache = (): void => {
    setClearStatus('clearing');
    void clearCachedGameData()
      .then(() => refreshCachedGameIds())
      .then(() => {
        setClearStatus('cleared');
      });
  };

  return (
    <Screen title="Settings">
      {affordance.kind !== 'none' && (
        <section class="settings__section" aria-labelledby="settings-install-heading">
          <h2 id="settings-install-heading">Install</h2>
          {affordance.kind === 'prompt' ? (
            <button
              type="button"
              class="settings__install-button"
              onClick={() => void promptInstall()}
            >
              Install Puzzle Gate
            </button>
          ) : (
            <p>
              <a class="nav-link" href={affordance.href} target="_blank" rel="noreferrer">
                {affordance.label}
              </a>
            </p>
          )}
        </section>
      )}

      <section class="settings__section" aria-labelledby="settings-storage-heading">
        <h2 id="settings-storage-heading">Storage</h2>
        {/* PWA-053. Three real states, not two: a browser that has not
            granted persistence yet reads differently from one that cannot
            be asked at all (no Storage API, `persisted` staying `null`). */}
        <p>
          {persisted === true &&
            'Storage is persistent -- downloaded games and levels are protected from automatic eviction.'}
          {persisted === false &&
            'Storage is not persistent yet. It will be requested the first time you complete a level.'}
          {persisted === null && "This browser doesn't report storage persistence."}
        </p>
        <p>
          Clearing cached game data frees space by removing downloaded games and levels. Your saved
          progress is not affected.
        </p>
        <button
          type="button"
          class="settings__button"
          onClick={handleClearCache}
          disabled={clearStatus === 'clearing'}
        >
          Clear cached game data
        </button>
        {clearStatus === 'cleared' && <p>Cleared.</p>}
      </section>

      <section class="settings__section" aria-labelledby="settings-diagnostics-heading">
        <h2 id="settings-diagnostics-heading">Diagnostics</h2>
        {/* PWA-060. package.json's own version, substituted at build time
            -- vite.config.ts's own comment has the reasoning. */}
        <p>Version {__APP_VERSION__}</p>
        {/* PWA-061. Every known game, not only cached ones -- "which games
            are available offline" is a question with a real answer for a
            game that is NOT cached too. */}
        <ul class="settings__game-status">
          {KNOWN_GAME_IDS.map((gameId) => {
            const info = gameInfo(gameId);
            if (!info) return null;
            return (
              <li key={gameId}>
                {info.name}: {cached.has(gameId) ? 'available offline' : 'not downloaded'}
              </li>
            );
          })}
        </ul>
      </section>

      <section class="settings__section" aria-labelledby="settings-troubleshooting-heading">
        <h2 id="settings-troubleshooting-heading">Troubleshooting</h2>
        <p>
          If the app is showing an old or broken version, reset it. Your saved progress is not
          affected.
        </p>
        <a class="nav-link" href="/reset.html">
          Reset the app
        </a>
      </section>

      <nav class="screen-nav">
        <a class="nav-link" href={routes.picker()}>
          Back to the game picker
        </a>
      </nav>
    </Screen>
  );
}
